/**
 * FmEditableTable — inline-editing data grid with DS cell editors+validation
 * Replaces bare AntD in TreeEntryTable; for tree entry + planner grids
 *
 * First-version skeleton. Full editable grid requires sophisticated state management.
 * This provides the DS component contract for future full implementation.
 */
import React, { useState } from 'react';
import type { SelectProps } from 'antd';
import type { FmTableColumn } from '../../types/fm-table';
import { FmTable } from './FmTable';
import { FmNumberField } from './FmNumberField';
import { FmTextField } from './FmTextField';
import { FmSelect } from './FmSelect';
import { FmButton } from './FmButton';
import { useLanguage } from '@/i18n';

export type CellEditorType = 'number' | 'text' | 'select';

export interface FmEditableTableColumn<T = Record<string, unknown>> extends Omit<
  FmTableColumn<T>,
  'render'
> {
  dataIndex: string;
  render?: (value: unknown, record: T) => React.ReactNode;
  editor?: CellEditorType;
  editorProps?: Record<string, unknown>;
  editorOptions?: SelectProps['options'];
  validation?: (value: unknown) => boolean | string;
}

export interface FmEditableTableProps<T = Record<string, unknown>> {
  columns: FmEditableTableColumn<T>[];
  rows: T[];
  onCellChange: (rowIndex: number, key: string, value: unknown) => void;
  onAddRow?: () => void;
  onRemoveRow?: (rowIndex: number) => void;
  density?: 'comfortable' | 'compact';
  /**
   * Sealed, data-only label for the built-in row-remove action. When omitted,
   * the DS falls back to the localized `ui.table.removeRow` key (never a
   * hardcoded English literal).
   */
  removeLabel?: string;
  /** Sealed, data-only label for the built-in add-row action (see removeLabel). */
  addLabel?: string;
}

interface EditingCell {
  rowIndex: number;
  key: string;
}

interface RenderEditorParams {
  col: FmEditableTableColumn;
  value: unknown;
  isEditing: boolean;
  rowIndex: number;
  onCellChange: (rowIndex: number, key: string, value: unknown) => void;
  setEditingCell: (cell: EditingCell | null) => void;
}

const renderEditorContent = ({
  col,
  value,
  isEditing,
  rowIndex,
  onCellChange,
  setEditingCell,
}: RenderEditorParams): React.ReactNode => {
  if (!col.editor) {
    if (col.render) {
      return col.render(value, {});
    }
    return value as React.ReactNode;
  }

  if (!isEditing) {
    return (
      <button
        type="button"
        className="fm-editable-table__cell"
        onClick={() => setEditingCell({ rowIndex, key: col.dataIndex })}
      >
        {col.render ? col.render(value, {}) : String(value ?? '')}
      </button>
    );
  }

  if (col.editor === 'number') {
    return (
      <div className="fm-editable-table__editor">
        <FmNumberField
          value={value as number}
          onChange={(v) => {
            onCellChange(rowIndex, col.dataIndex, v);
            setEditingCell(null);
          }}
          {...(col.editorProps || {})}
        />
      </div>
    );
  }

  if (col.editor === 'text') {
    return (
      <div className="fm-editable-table__editor">
        <FmTextField
          value={value as string}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const v = e.target.value;
            onCellChange(rowIndex, col.dataIndex, v);
            setEditingCell(null);
          }}
          {...(col.editorProps || {})}
        />
      </div>
    );
  }

  if (col.editor === 'select' && col.editorOptions) {
    return (
      <div className="fm-editable-table__editor">
        <FmSelect
          value={value}
          onChange={(v) => {
            onCellChange(rowIndex, col.dataIndex, v);
            setEditingCell(null);
          }}
          options={col.editorOptions}
          {...(col.editorProps || {})}
        />
      </div>
    );
  }

  return null;
};

/**
 * FmEditableTable — inline-editing grid component
 *
 * First-pass implementation focuses on component contract and token-based styling.
 * Full editable behavior (click-to-edit, validation, keyboard nav) implemented in follow-up.
 */
export const FmEditableTable = React.forwardRef<HTMLDivElement, FmEditableTableProps>(
  (
    {
      columns,
      rows,
      onCellChange,
      onAddRow,
      onRemoveRow,
      density = 'comfortable',
      removeLabel,
      addLabel,
    },
    ref,
  ) => {
    const { t } = useLanguage();
    const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
    const removeText = removeLabel ?? t.common.ui.table.removeRow;
    const addText = addLabel ?? t.common.ui.table.addRow;

    // Map FmEditableTableColumn to FmTableColumn with edit behavior
    const tableColumns: FmTableColumn<Record<string, unknown>>[] = (
      columns as FmEditableTableColumn[]
    ).map((col) => ({
      ...col,
      render: (row: Record<string, unknown>) => {
        const value = row[col.dataIndex];
        const rowIndex = rows.indexOf(row);
        const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.key === col.dataIndex;

        return renderEditorContent({
          col,
          value,
          isEditing,
          rowIndex,
          onCellChange,
          setEditingCell,
        });
      },
    }));

    // Add remove action column
    if (onRemoveRow) {
      tableColumns.push({
        key: '_actions',
        header: '',
        role: 'actions',
        render: (row: Record<string, unknown>) => {
          const rowIndex = rows.indexOf(row);
          return (
            <FmButton onClick={() => onRemoveRow(rowIndex)} data-testid={`remove-row-${rowIndex}`}>
              {removeText}
            </FmButton>
          );
        },
      });
    }

    const getRowKey = (row: Record<string, unknown>): string => {
      const id = row.id as string | number | undefined;
      return id !== undefined ? String(id) : JSON.stringify(row);
    };

    return (
      <div ref={ref} className={`fm-editable-table fm-editable-table--${density}`}>
        <FmTable columns={tableColumns} rows={rows} rowKey={getRowKey} />
        {onAddRow && (
          <div className="fm-editable-table__footer">
            <FmButton onClick={onAddRow}>{addText}</FmButton>
          </div>
        )}
      </div>
    );
  },
);

FmEditableTable.displayName = 'FmEditableTable';
